import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Notice } from "@/components/site/Bits";

// One shared postcode gate for every public form. A postcode outside the service area is refused
// outright and the wording comes from the server, so it can never drift between forms.
export function useServiceArea(postcode, purpose = "booking") {
  const [area, setArea] = useState(null);
  const [checking, setChecking] = useState(false);
  const request = useRef(0);

  useEffect(() => {
    const entered = (postcode || "").replace(/\s+/g, "");
    if (entered.length < 3) {
      request.current += 1;
      setArea(null);
      setChecking(false);
      return undefined;
    }
    const token = (request.current += 1);
    setChecking(true);
    const timer = setTimeout(() => {
      api
        .get("/postcode-check", { params: { postcode: entered, purpose } })
        .then(({ data }) => {
          if (token === request.current) {
            setArea(data);
            setChecking(false);
          }
        })
        .catch(() => {
          if (token === request.current) {
            setArea(null);
            setChecking(false);
          }
        });
    }, 450);
    return () => clearTimeout(timer);
  }, [postcode, purpose]);

  return {
    area,
    checking,
    covered: area?.covered === true,
    refused: area?.covered === false,
  };
}

export const ServiceAreaNotice = ({ area, checking }) => {
  if (checking)
    return (
      <p className="text-xs text-pg-muted" data-testid="service-area-checking">
        Checking your postcode…
      </p>
    );
  if (!area) return null;
  if (area.covered)
    return (
      <Notice testId="service-area-covered">
        {area.postcode} is within our service area
        {area.area ? ` (${area.area.name})` : ""}.
      </Notice>
    );
  return (
    <Notice tone="error" testId="service-area-refused">
      {area.reason}
    </Notice>
  );
};
